import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../providers/dataProvider';

export interface TranslationsFieldLocale {
  code: string;
  label: string;
}


/** Module-level cache so all components share the same data. */
let cachedLocales: TranslationsFieldLocale[] | null = null;

/** True when locale is the default/English content stored on top-level fields. */
export function isDefaultLocale(code: string): boolean {
  const normalized = code.toLowerCase().replace('_', '-');
  return normalized === 'en' || normalized.startsWith('en-');
}

export const useActiveLocales = (): {
  locales: TranslationsFieldLocale[];
  loading: boolean;
} => {
  const [locales, setLocales] = useState<TranslationsFieldLocale[]>(cachedLocales ?? []);
  const [loading, setLoading] = useState(cachedLocales === null);

  useEffect(() => {
    if (cachedLocales !== null) {
      setLocales(cachedLocales);
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('access_token');

    fetch(`${API_BASE_URL}/languages`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch locales: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        // API may return { data: [...] } or a plain array
        const list: { code: string; label: string; isActive?: boolean }[] =
          Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];

        // Only include active languages
        const active: TranslationsFieldLocale[] = list
          .filter((l) => l.isActive !== false)
          .map((l) => ({ code: l.code, label: l.label }));

        cachedLocales = active;
        setLocales(active);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  return { locales, loading };
};
